import PropTypes from "prop-types";
import React, { useReducer } from "react";

import API from "../../API";
import {
  SET_ACTIVE_RATE_PLAN,
  SET_ACTIVE_TOAST_MESSAGES,
  SET_GENERAL_SETTINGS_FORM,
  SET_BILLING_SETTINGS_FORM,
  SET_MESSAGING_SETTINGS_FORM,
  SET_PASSES_SETTINGS_FORM,
  POPULATE_PASS_OFFERS,
  POPULATE_PASS_OFFERS_COUNT,
  POPULATE_PASS_OFFER_TYPES,
  SET_SETTINGS_INVALIDS,
  SET_SORT_BY,
  SET_ORDER_BY,
  SET_ROWS_PER_PAGE,
  SET_PAGE_NUMBER,
  SET_HIDE_DISABLED,
  POPULATE_FIRMWARE_PACKAGES,
  POPULATE_GEOFENCE_PACKAGES,
  POPULATE_MARKETS,
  POPULATE_FINE_TYPES,
} from "../types";
import FleetSettingsContext from "./fleetSettingsContext";
import fleetSettingsReducer from "./fleetSettingsReducer";
import {
  fetchFirmwarePackages as _fetchFirmwarePackages,
  fetchGeofencePackages as _fetchGeofencePackages,
} from "../shared";
import {
  generalFleetSettingsSchema,
  billingFleetSettingsSchema,
  messagingFleetSettingsSchema,
  passesFleetSettingsSchema,
} from "../../validation/schemas";
import { FLEET_SETTING_PAGES } from "../../constants";
import { StringUtils } from "../../util";

const DEFAULT_TABLE_ROWS = 50;

const FleetSettingsState = ({ children }) => {
  const schemas = {
    general: generalFleetSettingsSchema,
    billing: billingFleetSettingsSchema,
    messaging: messagingFleetSettingsSchema,
    passes: passesFleetSettingsSchema,
  };

  const initialState = {
    activeRatePlan: null,
    activeToastMessages: null,
    billing: {
      currency: "USD",
      flat_fee: "",
      time_unit_fee: "",
      first_card_bonus: "",
      auto_renew_options: [],
      tax: "",
      fees: [],
      rate_plan_fine_types: [],
      blocking_amount: "",
      min_limit_on_card_no_hold: "",
      min_limit_no_card: "",
      group_rides_hold_adjustment: 0.0,
    },
    general: {
      critical_battery_threshold: "",
      slow_first_ride: false,
      slow_rides_count: 0,
      first_ride_speed: "",
      operating_hours_begin: "",
      operating_hours_soft_end: "",
      warn_soft_end: "",
      require_parking_photo: false,
      enable_sidewalk_notifications: false,
      lock_to_enabled: false,
      minimum_age: "",
      required_jumio_level: "none",
      deployment_reservation_limit: "",
      required_survey_template_uuid: "",
      required_survey_ride_count: 0,
      required_survey_refresh_days: 0,
      notification_email: "",
      default_firmware_package: "",
      default_geofence_package: "",
      market: "",
      survey_enabled: true,
      first_survey_offset: 1,
      max_active_reservations_per_group: 0,
      max_total_reservations_per_group: 0,
      is_bookable: false,
      platform: "SUPERPEDESTRIAN",
    },
    messaging: {
      id: null,
      message: "",
      hyperlink: "",
      dismissable: true,
      begin: "",
      end: "",
      fleets: [],
    },
    passOffers: null,
    passOffersCount: 0,
    passOfferTypes: null,
    passes: {
      pass_offer_type_id: "",
      description: "",
      price: "",
      currency: "USD",
      start_date: "",
      end_date: "",
      fleet: "",
      disabled: false,
      end_date__gte: new Date().toISOString(),
      visible: false,
    },
    invalids: {
      general: [],
      billing: [],
      messaging: [],
      passes: [],
    },
    sortBy: "pass_offer_type__length",
    orderBy: "asc",
    pageNumber: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS,
    hideDisabled: true,
    firmwarePackages: [],
    geofencePackages: [],
    markets: [],
    fineTypes: [],
  };

  const [state, dispatch] = useReducer(fleetSettingsReducer, initialState);

  const setPageNumber = (pageNum) =>
    dispatch({
      type: SET_PAGE_NUMBER,
      payload: pageNum,
    });

  const setRowsPerPage = (rowsPerPage) =>
    dispatch({
      type: SET_ROWS_PER_PAGE,
      payload: rowsPerPage,
    });

  const setHideDisabled = (hideDisabled) => {
    dispatch({
      type: SET_HIDE_DISABLED,
      payload: hideDisabled,
    });
  };

  const prepareToastMessages = (messages = []) =>
    messages.map((msg) => {
      msg.id = StringUtils.uuidFromUrl(msg.url);
      msg.message = msg.message || msg.custom_message;
      return msg;
    });

  const fetchRatePlan = async (id) => {
    if (!id) return;
    try {
      const ratePlan = await API.billing.getRatePlan(id);
      dispatch({
        type: SET_ACTIVE_RATE_PLAN,
        payload: ratePlan,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const updateActiveRatePlan = async (id, data) => {
    let ratePlan = null;
    if (!id || !data) return ratePlan;
    try {
      ratePlan = await API.billing.updateRatePlan(id, data);
      if (ratePlan?.auto_renew_options?.length)
        // sort amount then sub-sort bonus
        ratePlan.auto_renew_options = ratePlan.auto_renew_options.sort(
          (a, b) => Number(a.amount) - Number(b.amount) || Number(a.bonus) - Number(b.bonus),
        );
      if (ratePlan?.fees?.length)
        // sort name then sub-sort amount
        ratePlan.fees = ratePlan.fees.sort(
          (a, b) => a.name.localeCompare(b.name) || Number(a.amount) - Number(b.amount),
        );
      dispatch({
        type: SET_ACTIVE_RATE_PLAN,
        payload: ratePlan,
      });
    } catch (err) {
      console.error(err);
    }
    return ratePlan;
  };

  const fetchToastMessages = async (fleetId) => {
    if (!fleetId) return;
    try {
      const messages = await API.messaging.list(fleetId);

      dispatch({
        type: SET_ACTIVE_TOAST_MESSAGES,
        payload: prepareToastMessages(messages),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPassOffers = async (
    params = {
      fleet: null,
      ordering: null,
      disabled: null,
      page_size: 50,
      page: 1,
    },
  ) => {
    if (!params.fleet) {
      dispatch({ type: POPULATE_PASS_OFFERS, payload: null });
      return;
    }
    try {
      const passOffersByFleet = await API.billing.getPassOffers(params);
      dispatch({
        type: POPULATE_PASS_OFFERS,
        payload: passOffersByFleet.results,
      });
      dispatch({
        type: POPULATE_PASS_OFFERS_COUNT,
        payload: passOffersByFleet.count,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getToastMessage = async (id) => {
    const message = await API.messaging.get(id);

    dispatch({
      type: SET_MESSAGING_SETTINGS_FORM,
      payload: {
        id,
        message: message.custom_message,
        hyperlink: message.hyperlink,
        dismissable: !!message.dismissable,
        begin: message.begin,
        end: message.end,
        fleets: message.fleets,
      },
    });
  };

  const uploadToastMessage = async (fleetUrl, data) => {
    if (!fleetUrl || !data) return [];
    try {
      const payload = {
        id: data.id,
        message: "",
        custom_message: data.message,
        hyperlink: data.hyperlink,
        dismissable: data.dismissable,
        begin: data.begin,
        end: data.end,
        fleets: [...data.fleets, fleetUrl],
        override: data.override,
        multiple: true,
      };

      const errors = data.id
        ? await API.messaging.edit(data.id, payload)
        : await API.messaging.create(payload);

      return prepareToastMessages(errors);
    } catch (err) {
      if (err.response?.status === 400) {
        if (Array.isArray(err.response.data)) return prepareToastMessages(err.response.data);
        return { error: err.response.data };
      }
      return [];
    }
  };

  const fetchPassOfferTypes = async () => {
    try {
      const allPassOfferTypes = await API.billing.getPassOfferTypes();
      dispatch({
        type: POPULATE_PASS_OFFER_TYPES,
        payload: allPassOfferTypes.results,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const passOfferAlreadyExists = async (
    params = {
      fleet: null,
      pass_offer_type_id: null,
      start_date: null,
      end_date: null,
      disabled: false,
    },
  ) => {
    if (!params.fleet || !params.pass_offer_type_id) return null;
    try {
      const resArr = await API.billing.getPassOffers(params);
      return resArr.some((val) => val?.results?.length);
    } catch (err) {
      console.error(err);
      return true;
    }
  };

  const createNewPassOffer = async (data) => {
    try {
      const newPassOffer = await API.billing.createPassOffer(data);
      dispatch({
        type: POPULATE_PASS_OFFERS,
        payload: [...state.passOffers, newPassOffer],
      });
      return newPassOffer;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const updatePassOffer = async (id, data) => {
    let updatedPassOffer = null;
    try {
      updatedPassOffer = await API.billing.updatePassOffer(id, data);
      return updatedPassOffer;
    } catch (err) {
      console.error(err);
    }
    return updatedPassOffer;
  };

  const setBillingSettingsForm = (formData) => {
    dispatch({
      type: SET_BILLING_SETTINGS_FORM,
      payload: formData,
    });
  };

  const setGeneralSettingsForm = (formData) => {
    dispatch({
      type: SET_GENERAL_SETTINGS_FORM,
      payload: formData,
    });
  };

  const setMessagingSettingsForm = (formData) => {
    dispatch({
      type: SET_MESSAGING_SETTINGS_FORM,
      payload: formData || { ...initialState.messaging },
    });
  };

  const setPassesSettingsForm = (formData) => {
    dispatch({
      type: SET_PASSES_SETTINGS_FORM,
      payload: formData || { ...initialState.passes },
    });
  };

  const validateForm = async (formName) => {
    if (!FLEET_SETTING_PAGES.includes(formName)) return;
    try {
      await schemas[formName].validate(state[formName], {
        abortEarly: false,
        recursive: true,
        strict: false, // will cast before validation
      });
      dispatch({
        type: SET_SETTINGS_INVALIDS,
        payload: { ...state.invalids, [formName]: [] },
      });
    } catch (err) {
      dispatch({
        type: SET_SETTINGS_INVALIDS,
        payload: { ...state.invalids, [formName]: err.errors },
      });
    }
  };

  const setSortBy = (sortBy) =>
    dispatch({
      type: SET_SORT_BY,
      payload: sortBy,
    });

  const setOrderBy = (orderBy) =>
    dispatch({
      type: SET_ORDER_BY,
      payload: orderBy,
    });

  const ordering = () => (state.orderBy === "asc" ? state.sortBy : `-${state.sortBy}`);

  const refreshPassOffers = async (fleet, page = null) => {
    await fetchPassOffers({
      fleet: fleet.uuid4,
      ...(state.hideDisabled ? { disabled: false, end_date__gte: new Date().toISOString() } : {}),
      ordering: ordering(),
      page_size: state.rowsPerPage,
      page: page ?? state.pageNumber + 1,
    });
  };

  const fetchFirmwarePackages = async () => {
    const fwp = await _fetchFirmwarePackages();
    dispatch({
      type: POPULATE_FIRMWARE_PACKAGES,
      payload: fwp,
    });
  };

  const fetchGeofencePackages = async (params, forceUpdate) => {
    const [geoPacks] = await _fetchGeofencePackages(params, forceUpdate);
    dispatch({
      type: POPULATE_GEOFENCE_PACKAGES,
      payload: geoPacks,
    });
  };

  const fetchMarkets = async () => {
    const markets = await API.markets.list();
    dispatch({
      type: POPULATE_MARKETS,
      payload: markets,
    });
  };

  const fetchFineTypes = async () => {
    const fineTypes = await API.billing.listFineTypes();
    dispatch({
      type: POPULATE_FINE_TYPES,
      payload: fineTypes,
    });
  };

  return (
    <FleetSettingsContext.Provider
      value={{
        activeRatePlan: state.activeRatePlan,
        activeToastMessages: state.activeToastMessages,
        billing: state.billing,
        general: state.general,
        messaging: state.messaging,
        passOffers: state.passOffers,
        passOffersCount: state.passOffersCount,
        passOfferTypes: state.passOfferTypes,
        passes: state.passes,
        invalids: state.invalids,
        sortBy: state.sortBy,
        orderBy: state.orderBy,
        pageNumber: state.pageNumber,
        rowsPerPage: state.rowsPerPage,
        hideDisabled: state.hideDisabled,
        firmwarePackages: state.firmwarePackages,
        geofencePackages: state.geofencePackages,
        markets: state.markets,
        fleet_group: state.fleet_group,
        fineTypes: state.fineTypes,
        schemas,
        fetchRatePlan,
        updateActiveRatePlan,
        fetchPassOffers,
        fetchPassOfferTypes,
        passOfferAlreadyExists,
        createNewPassOffer,
        updatePassOffer,
        setBillingSettingsForm,
        setGeneralSettingsForm,
        setMessagingSettingsForm,
        fetchToastMessages,
        getToastMessage,
        uploadToastMessage,
        setPassesSettingsForm,
        validateForm,
        refreshPassOffers,
        setSortBy,
        setOrderBy,
        setRowsPerPage,
        setPageNumber,
        setHideDisabled,
        fetchGeofencePackages,
        fetchFirmwarePackages,
        fetchMarkets,
        fetchFineTypes,
      }}
    >
      {children}
    </FleetSettingsContext.Provider>
  );
};

FleetSettingsState.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FleetSettingsState;
